/**
 * Account Page
 *
 * Displays current plan details, subscription status, and account deletion UI.
 * Syncs subscription data on load and updates user profile when needed.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SERVER_URL } from '../../api/core';
import { auth } from '../../firebase/firebase';
import { useAuth } from '../../context/auth';
import PlanSection from '../../components/account/account/PlanSection';
import BillingSection from '../../components/account/account/BillingSection';
import DeleteAccountSection from '../../components/account/account/DeleteAccountSection';
import Footer from '../../components/shared/Footer';
import { syncSubscriptionData } from '../../services/subscriptionSyncService';
import SEOHead from '../../components/shared/SEOHead';

export default function Account() {
  const { user, setUser } = useAuth();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState({
    plan: { success: '' }
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is enterprise - ONLY by database flag, NOT by email domain
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;

  // Users with a Stripe customer can use the billing portal (receipts, payment
  // methods, cancellation). Enterprise billing is managed by the organization.
  const hasStripeBilling = !isEnterpriseUser && !!user?.stripeCustomerId;

  // Function to format date - moved up before it's used
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Handle Firestore timestamp objects that might come as objects with _seconds and _nanoseconds
    if (typeof dateString === 'object' && dateString._seconds) {
      const date = new Date(dateString._seconds * 1000);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    // Handle ISO string dates
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date unavailable';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date unavailable';
    }
  };

  // Get the current user's plan - updated to use planLevel
  const userPlanLevel = user?.planLevel || 'standard';
  const userPlanInterval = user?.planInterval || null;
  
  // Get subscription status information - updated to match new server structure
  const subscriptionStatus = user?.subscriptionStatus || 'active';
  const subscriptionPeriodEnd = user?.subscriptionPeriodEnd || null;

  // Format dates for display
  const formattedSubscriptionPeriodEnd = subscriptionPeriodEnd ? formatDate(subscriptionPeriodEnd) : null;

  console.log("Account - Current user:", user);
  console.log("Account - Current plan level:", userPlanLevel);
  console.log("Account - Current plan interval:", userPlanInterval);
  console.log("Account - Subscription status:", subscriptionStatus);
  console.log("Account - Subscription period end:", subscriptionPeriodEnd);
  console.log("Account - Formatted subscription period end:", formattedSubscriptionPeriodEnd);

  // Sync subscription data when component mounts
  useEffect(() => {
    // Skip Stripe sync for enterprise users since they don't use Stripe billing
    if (isEnterpriseUser) {
      console.log('Account page loaded - skipping Stripe sync for enterprise user');
      return;
    }
    
    console.log('Account page loaded, syncing subscription data');
    syncSubscriptionData(handleSyncComplete, false, user)
      .catch(err => console.error('Error syncing subscription on account page load:', err));
  }, [isEnterpriseUser]);

  // Check if plan was just updated
  useEffect(() => {
    if (location.state?.planUpdated) {
      const planLevel = location.state.plan;
      const previousPlan = location.state.previousPlan;
      const isDowngrade =
        previousPlan &&
        (previousPlan === 'premium' && planLevel === 'standard');

      setMessages(prev => ({
        ...prev,
        plan: {
          success: isDowngrade
            ? `Your subscription has been successfully downgraded to the ${getPlanDisplayName(planLevel)}.`
            : `Your subscription has been successfully upgraded to the ${getPlanDisplayName(planLevel)}!`
        }
      }));

      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessages(prev => ({
          ...prev,
          plan: { success: '' }
        }));
      }, 5000);

      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Function to get plan display name
  const getPlanDisplayName = (planLevel) => {
    if (planLevel === 'standard' || planLevel === 'basic') return 'Standard Plan';
    if (planLevel === 'premium') return 'Premium Plan';
    return planLevel.charAt(0).toUpperCase() + planLevel.slice(1) + ' Plan';
  };

  // Function to get plan interval display name
  const getPlanIntervalDisplayName = (interval) => {
    if (!interval) return '';
    if (interval === 'monthly') return 'Monthly';
    if (interval === 'annual') return 'Annual';
    return interval.charAt(0).toUpperCase() + interval.slice(1);
  };

  // Function to navigate to pricing page
  const handleNavigateToPricing = () => {
    console.log("Navigating to pricing page");
    navigate('/app/account/pricing');
  };

  const handleDeleteAccount = async () => {
    try {
      setError('');
      setIsLoading(true);
      
      // Clear application data immediately
      localStorage.removeItem('refrral_copy_analysis');
      localStorage.removeItem('refrral_generated_release');
      localStorage.clear();
      sessionStorage.clear();
      
      // Simply set the modal open state, actual deletion is handled by DeleteAccountSection
      setIsDeleteModalOpen(true);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('An error occurred while deleting your account.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sync completion - refresh user data
  const handleSyncComplete = async (syncResult) => {
    try {
      console.log('Subscription sync completed:', syncResult);
      
      // If we have updated subscription data, update user state
      if (syncResult.synced && syncResult.subscription) {
        // Refresh user token to ensure we get the latest claims
        await auth.currentUser.getIdToken(true);
        
        // Get fresh user data using the correct endpoint
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(`${SERVER_URL}/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to refresh user data');
        }
        
        const userData = await response.json();
        console.log('Refreshed user data after sync:', userData);
        
        // Update the user context with fresh data
        if (userData) {
          setUser(userData);
        }
      }
    } catch (err) {
      console.error('Error refreshing user data after sync:', err);
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-6">
      <SEOHead pageType="app-account" />
      <PlanSection
        userPlanLevel={userPlanLevel}
        userPlanInterval={userPlanInterval}
        planSuccessMessage={messages.plan.success}
        getPlanDisplayName={getPlanDisplayName}
        getPlanIntervalDisplayName={getPlanIntervalDisplayName}
        handleNavigateToPricing={handleNavigateToPricing}
        subscriptionPeriodEnd={formattedSubscriptionPeriodEnd}
        formatDate={formatDate}
        isEnterpriseUser={isEnterpriseUser}
      />

      {hasStripeBilling && <BillingSection />}

      <DeleteAccountSection
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        isLoading={isLoading}
        error={error}
        handleDeleteAccount={handleDeleteAccount}
        showCancelAlternative={hasStripeBilling && userPlanLevel === 'premium'}
      />

      <Footer />
    </div>
  );
}